import { Col, Form, Row } from "antd";
import { CompetencyRole } from "common/Constants";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { castArray } from "lodash";
import { setFilter } from "redux/actions";
import { getFilters, getSupervisorList, getGradeList } from "redux/selectors";
import styled from "styled-components";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";

// Set to true temporarily to see what each filter compares in the console.
const DEBUG_EMPLOYEE_FILTER = false;

// Case / whitespace insensitive normaliser
const norm = (v) => String(v).trim().toLowerCase();

/**
 * Turn a record field (string | array | object | undefined) into a flat list
 * of normalised tokens. Comma-joined strings ("Cameroon, DR Congo") are also
 * split, so they match either as a whole or per part.
 */
const toTokens = (value) => {
  const out = [];
  castArray(value).forEach((raw) => {
    let v = raw;
    if (v === null || v === undefined || v === "") return;
    if (typeof v === "object") v = v.value ?? v.label ?? v.name ?? "";
    const s = norm(v);
    if (!s) return;
    out.push(s);
    if (s.includes(",")) {
      s.split(",").forEach((part) => {
        const t = part.trim();
        if (t) out.push(t);
      });
    }
  });
  return out;
};

// true when nothing is selected, or when the record shares >= 1 value with the
// selection (OR inside a single filter)
const matchesAny = (fieldValue, selected) => {
  if (!selected || selected.length === 0) return true;
  const tokens = toTokens(fieldValue);
  if (tokens.length === 0) return false;
  return selected.some((s) => tokens.includes(norm(s)));
};

// First defined, non-null value among several possible key names
const pick = (item, ...keys) => {
  for (const k of keys) {
    if (item?.[k] !== undefined && item?.[k] !== null) return item[k];
  }
  return undefined;
};

// All string tokens found anywhere in a record (cached per record object)
const tokenCache = new WeakMap();
const collectAllTokens = (item) => {
  if (!item || typeof item !== "object") return [];
  if (tokenCache.has(item)) return tokenCache.get(item);
  const out = [];
  const walk = (v, depth) => {
    if (v === null || v === undefined) return;
    if (Array.isArray(v)) {
      if (depth < 3) v.forEach((x) => walk(x, depth + 1));
      return;
    }
    if (typeof v === "object") {
      if (depth < 3) Object.values(v).forEach((x) => walk(x, depth + 1));
      return;
    }
    out.push(...toTokens(v));
  };
  walk(item, 0);
  tokenCache.set(item, out);
  return out;
};

// Like matchesAny, but when the expected field is empty/missing on the record,
// fall back to looking for the selected value anywhere in the record.
const matchesField = (item, preferredValue, selected) => {
  if (!selected || selected.length === 0) return true;
  const tokens = toTokens(preferredValue);
  if (tokens.length > 0) return selected.some((s) => tokens.includes(norm(s)));
  const all = collectAllTokens(item);
  return selected.some((s) => all.includes(norm(s)));
};

// Find a value by scanning the record's keys (ignores case, "_" and "-")
const findByKeyPattern = (item, test) => {
  if (!item) return undefined;
  const key = Object.keys(item).find((k) =>
    test(k.toLowerCase().replace(/[^a-z]/g, ""))
  );
  return key ? item[key] : undefined;
};

// Competency / QC may be stored under different key names
const getCompetencyQC = (item) =>
  pick(item, "competencyQC", "competencyqc", "competency", "competencyQc") ??
  findByKeyPattern(item, (k) => k.includes("competency") && !k.includes("role"));

const getCompetencyRole = (item) =>
  pick(item, "competencyrole", "competencyRole") ??
  findByKeyPattern(item, (k) => k.includes("competency") && k.includes("role"));

const useEmployeeFilter = ({
  employeeFilterList,
  allEmployeeList,
  containerRef,
  managingOfficeData,
  competencyQCData,
  departmentLevelData,
  subservicelineData,
  servicelineData,
  STAFF_LIST,
  menuName,
  employeeType = "1",
  onFilterChange,
  showFilters = {},
}) => {
  const [form] = Form.useForm();
  const filters = useSelector(getFilters);
  const gradeListData = useSelector(getGradeList) || [];
  const Grade = gradeListData.map((item) => item.jobgradecode);

  // FIX: Team.jsx passes `managingOffice` (capital O) while this hook reads
  // `managingoffice`. Accept both, and default every filter to visible.
  const show = {
    employee: showFilters.employee ?? true,
    // Country: pass showFilters={{ managingOffice: false }} to hide it on a page
    managingoffice:
      showFilters.managingoffice ?? showFilters.managingOffice ?? true,
    competencyQC: showFilters.competencyQC ?? true,
    serviceline: showFilters.serviceline ?? true,
    team: showFilters.team ?? true,
    leveldepartmentname: showFilters.leveldepartmentname ?? true,
    competencyrole: showFilters.competencyrole ?? true,
    supervisor: showFilters.supervisor ?? true,
    grade: showFilters.grade ?? true,
  };

  const initialFilter = filters?.[menuName];
  // Current selections (kept in redux by handleFormChange)
  const activeFilters = filters?.[menuName] || {};

  const supervisorList = useSelector(getSupervisorList);
  const dispatch = useDispatch();

  const handleFormChange = () => {
    const values = form.getFieldsValue();

    const next = {
      employee: values.employee || [],
      supervisor: values.supervisor || [],
      competencyrole: values.competencyrole || [],
      managingoffice: show.managingoffice ? values.managingoffice || [] : [],
      leveldepartmentname: values.leveldepartmentname || [],
      competencyQC: values.competencyQC || [],
      serviceline: values.serviceline || [],
      team: values.team || [],
      grade: values.grade || [],
    };

    // Apply the cascading resets BEFORE dispatching, so redux and the form
    // always hold the same values.
    if (next.competencyQC.length === 0) {
      next.serviceline = [];
      next.team = [];
      next.leveldepartmentname = [];
    }
    if (next.serviceline.length === 0) {
      next.team = [];
      next.leveldepartmentname = [];
    }
    if (next.team.length === 0) {
      next.leveldepartmentname = [];
    }

    form.setFieldsValue({
      serviceline: next.serviceline,
      team: next.team,
      leveldepartmentname: next.leveldepartmentname,
    });

    Object.entries(next).forEach(([key, value]) => {
      dispatch(setFilter(menuName, key, value));
    });

    onFilterChange?.(form.getFieldsValue());
  };

  /**
   * Filters the list here: AND between different filters, OR between the
   * values selected inside one filter. Matching is case/whitespace
   * insensitive and tolerant of alternative field names.
   */
  const filteredList = useMemo(() => {
    const list = employeeFilterList || [];

    const {
      employee = [],
      supervisor = [],
      competencyrole = [],
      managingoffice = [],
      leveldepartmentname = [],
      competencyQC = [],
      serviceline = [],
      team = [],
      grade = [],
    } = activeFilters;

    if (DEBUG_EMPLOYEE_FILTER) {
      // eslint-disable-next-line no-console
      console.log("[useEmployeeFilter] selections", activeFilters);
      // eslint-disable-next-line no-console
      console.log("[useEmployeeFilter] sample record fields", {
        userid: list[0]?.userid,
        OfficeSupervisorId: list[0]?.OfficeSupervisorId,
        competencyKeysOnRecord: Object.keys(list[0] || {}).filter((k) =>
          /compet|qc/i.test(k)
        ),
        resolvedCompetencyQC: getCompetencyQC(list[0]),
        resolvedCompetencyRole: getCompetencyRole(list[0]),
        managingoffice: list[0]?.managingoffice,
        leveldepartmentname: list[0]?.leveldepartmentname,
        competencyQC: list[0]?.competencyQC,
        serviceline: list[0]?.serviceline,
        subserviceline: list[0]?.subserviceline,
        grade: list[0]?.grade,
      });
    }

    return list.filter(
      (item) =>
        matchesAny(item?.userid, employee) &&
        matchesAny(
          pick(item, "OfficeSupervisorId", "officesupervisorid"),
          supervisor
        ) &&
        matchesField(item, getCompetencyRole(item), competencyrole) &&
        (!show.managingoffice ||
          matchesField(item, item?.managingoffice, managingoffice)) &&
        matchesField(item, item?.leveldepartmentname, leveldepartmentname) &&
        matchesField(item, getCompetencyQC(item), competencyQC) &&
        matchesField(item, item?.serviceline, serviceline) &&
        matchesField(item, item?.subserviceline, team) && // "team" filter = Sub Serviceline
        matchesAny(item?.grade, grade)
    );
    // activeFilters comes from redux; its identity changes whenever a filter changes
  }, [employeeFilterList, activeFilters, show.managingoffice]);

  const getFilterList = () => filteredList;

  const iscompetencyQCSelcted = form.getFieldValue("competencyQC")?.length > 0;
  const isServicelineSelected = form.getFieldValue("serviceline")?.length > 0;
  const isTeamSelected = form.getFieldValue("team")?.length > 0;

  // People options must come from the UNFILTERED list, otherwise selecting
  // one person would remove everyone else from the dropdown.
  const peopleOptions = (allEmployeeList || employeeFilterList || [])
    .filter((item) => String(item?.statusid) === String(employeeType))
    .map((item) => ({
      label: item?.fullname,
      value: item?.userid,
    }));

  const FilterJSX = (
    <StyleWrap>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        initialValues={{ ...initialFilter }}
      >
        <Row gutter={16}>
          {show.managingoffice && (
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={6}
              xxl={4}
              ref={containerRef}
            >
              <DropdownCheckBox
                name="managingoffice"
                label="Country"
                options={managingOfficeData?.map((value) => ({
                  label: value,
                  value: value,
                }))}
                form={form}
                handleFormChange={handleFormChange}
              />
            </Col>
          )}

          {show.employee && (
            <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={4}>
              <DropdownCheckBox
                name="employee"
                label="People"
                options={peopleOptions}
                form={form}
                handleFormChange={handleFormChange}
              />
            </Col>
          )}

          {show.competencyQC && (
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={6}
              xxl={4}
              ref={containerRef}
            >
              <DropdownCheckBox
                name="competencyQC"
                label="Competency/ QC"
                options={competencyQCData?.map((value) => ({
                  label: value,
                  value: value,
                }))}
                form={form}
                handleFormChange={handleFormChange}
              />
            </Col>
          )}

          {show.serviceline && (
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={6}
              xxl={4}
              ref={containerRef}
            >
              <DropdownCheckBox
                name="serviceline"
                label="Serviceline"
                options={servicelineData?.map((value) => ({
                  label: value,
                  value: value,
                }))}
                form={form}
                handleFormChange={handleFormChange}
                disabled={!iscompetencyQCSelcted}
              />
            </Col>
          )}

          {show.team && (
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={6}
              xxl={4}
              ref={containerRef}
            >
              <DropdownCheckBox
                name="team"
                label="Sub Serviceline"
                options={subservicelineData?.map((value) => ({
                  label: value,
                  value: value,
                }))}
                form={form}
                handleFormChange={handleFormChange}
                disabled={!isServicelineSelected}
              />
            </Col>
          )}

          {show.leveldepartmentname && (
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={6}
              xxl={4}
              ref={containerRef}
            >
              <DropdownCheckBox
                name="leveldepartmentname"
                label="Department"
                options={[...new Set(departmentLevelData)]?.map((value) => ({
                  label: value,
                  value: value,
                }))}
                form={form}
                handleFormChange={handleFormChange}
                disabled={!isTeamSelected}
              />
            </Col>
          )}

          {show.grade && (
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={6}
              xxl={4}
              ref={containerRef}
            >
              <DropdownCheckBox
                name="grade"
                label="Grade"
                options={Grade?.map((value) => ({
                  label: value,
                  value: value,
                }))}
                form={form}
                handleFormChange={handleFormChange}
              />
            </Col>
          )}

          {show.competencyrole && (
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={6}
              xxl={4}
              ref={containerRef}
            >
              <DropdownCheckBox
                name="competencyrole"
                label="Competency Role"
                options={CompetencyRole?.map((item) => ({
                  label: item,
                  value: item,
                }))}
                form={form}
                handleFormChange={handleFormChange}
              />
            </Col>
          )}

          {show.supervisor && (
            <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={4}>
              <DropdownCheckBox
                name="supervisor"
                label="Office Supervisor"
                options={supervisorList?.map((item) => ({
                  label: item?.name,
                  value: item?.employeeId,
                }))}
                form={form}
                handleFormChange={handleFormChange}
              />
            </Col>
          )}
        </Row>
      </Form>
    </StyleWrap>
  );

  return {
    FilterJSX,
    getFilterList,
  };
};

export default useEmployeeFilter;

const StyleWrap = styled.div`
  .ant-form-item {
    margin-bottom: 16px !important;
  }
`;